import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { customerApi, scheduleApi } from '../api';
import {
  CUSTOMER_STATUSES, CUSTOMER_STATUS_COLORS,
  CUSTOMER_TYPES, CUSTOMER_TYPE_COLORS,
  REFERRAL_SOURCES, REFERRAL_SOURCE_DETAILS,
  DATE_CRITERIA, SEARCH_CRITERIA,
  SCHEDULE_TYPE_OPTIONS,
} from '../constants';

interface MakeupCustomer {
  makeupCustomerIdx: number;
  groomName?: string;
  groomPhone?: string;
  brideName?: string;
  bridePhone?: string;
  customerType?: string;
  customerStatus: string;
  orderDt?: string;
  rehearsalDt?: string;
  weddingDt?: string;
  weddingVenue?: string;
  partnerVendorName?: string;
  partnerStaffName?: string;
  rehearsalMakeupStaff?: { userIdx: number; name: string };
  mainMakeupStaff?: { userIdx: number; name: string };
  rehearsalHairStaff?: { userIdx: number; name: string };
  mainHairStaff?: { userIdx: number; name: string };
  honjuInfo?: string;
  dressShopName?: string;
  scheduleType?: string;
  referralSource?: string;
  memo?: string;
  createDt: string;
}

export default function MakeupShopCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<MakeupCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCriteria, setSearchCriteria] = useState('all');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateCriteria, setDateCriteria] = useState('orderDt');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [makeupStaffSearch, setMakeupStaffSearch] = useState('');
  const [makeupStaffType, setMakeupStaffType] = useState<'rehearsal' | 'main'>('rehearsal');
  const [hairStaffSearch, setHairStaffSearch] = useState('');
  const [hairStaffType, setHairStaffType] = useState<'rehearsal' | 'main'>('rehearsal');
  const [showAddModal, setShowAddModal] = useState(false);

  const [form, setForm] = useState({
    groomName: '', groomPhone: '', brideName: '', bridePhone: '',
    customerType: '', customerStatus: '발주',
    orderDt: '', rehearsalDt: '', weddingDt: '', weddingVenue: '',
    honjuInfo: '', dressShopName: '', scheduleType: '',
    referralSource: '', referralSourceDetail: '', memo: '',
  });

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await customerApi.list({
        q: searchQuery, searchCriteria, customerType: typeFilter,
        dateCriteria, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
        vendorName: vendorName || undefined,
        makeupStaff: makeupStaffSearch || undefined, makeupStaffType,
        hairStaff: hairStaffSearch || undefined, hairStaffType,
        page, size: 20,
      });
      setCustomers(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('고객 목록 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, searchCriteria, typeFilter, dateCriteria, dateFrom, dateTo, vendorName, makeupStaffSearch, makeupStaffType, hairStaffSearch, hairStaffType, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.groomName && !form.brideName) { alert('신랑 또는 신부 이름은 필수입니다.'); return; }
    if (!form.groomPhone && !form.bridePhone) { alert('신랑 또는 신부 연락처는 필수입니다.'); return; }
    try {
      const customer = await customerApi.create(form);
      const custIdx = customer?.makeupCustomerIdx;

      if (custIdx) {
        const schedulePromises: Promise<any>[] = [];
        const custName = form.groomName || form.brideName;
        const custPhone = form.groomPhone || form.bridePhone;

        if (form.rehearsalDt) {
          schedulePromises.push(scheduleApi.create({
            section: form.scheduleType || 'SHOP', scheduleCategory: 'REHEARSAL',
            title: `${custName} 리허설`, startDt: form.rehearsalDt, endDt: form.rehearsalDt,
            makeupCustomerIdx: custIdx, customerName: custName, customerPhone: custPhone,
          }).catch(() => {}));
        }
        if (form.weddingDt) {
          schedulePromises.push(scheduleApi.create({
            section: form.scheduleType || 'ONSITE', scheduleCategory: 'MAIN_CEREMONY',
            title: `${custName} 본식`, startDt: form.weddingDt, endDt: form.weddingDt,
            makeupCustomerIdx: custIdx, customerName: custName, customerPhone: custPhone,
          }).catch(() => {}));
        }
        await Promise.all(schedulePromises);
      }

      alert('고객이 등록되었습니다.');
      setShowAddModal(false);
      setForm({
        groomName: '', groomPhone: '', brideName: '', bridePhone: '',
        customerType: '', customerStatus: '발주',
        orderDt: '', rehearsalDt: '', weddingDt: '', weddingVenue: '',
        honjuInfo: '', dressShopName: '', scheduleType: '',
        referralSource: '', referralSourceDetail: '', memo: '',
      });
      fetchCustomers();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  const formatDate = (dt?: string) => {
    if (!dt) return '미정';
    return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const daysUntil = (dt?: string) => {
    if (!dt) return null;
    const diff = Math.ceil((new Date(dt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return null;
    return diff;
  };

  const totalPages = Math.ceil(total / 20);
  const referralDetailOptions = REFERRAL_SOURCE_DETAILS[form.referralSource] || [];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객관리</h1>
            <p className="text-sm text-gray-600 mt-1">메이크업 고객을 관리합니다</p>
          </div>
          <button onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 cursor-pointer">
            <i className="ri-add-line"></i> 고객 등록
          </button>
        </div>

        {/* 필터 영역 */}
        <div className="bg-white p-5 rounded-lg shadow-sm mb-6">
          {/* Row 1 */}
          <div className="flex items-end gap-4 flex-wrap">
            {/* 고객구분 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">고객구분</label>
              <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
                <option value="">전체</option>
                {CUSTOMER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {/* 업체명 조회 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">업체명조회</label>
              <input type="text" placeholder="업체명 검색" value={vendorName}
                onChange={(e) => { setVendorName(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[140px]" />
            </div>
            {/* 메이크업담당 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">메이크업담당</label>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-sm">
                  <input type="radio" name="makeupStaffType" value="rehearsal" checked={makeupStaffType === 'rehearsal'}
                    onChange={() => setMakeupStaffType('rehearsal')} className="accent-blue-600" />
                  리허설
                </label>
                <label className="flex items-center gap-1 text-sm">
                  <input type="radio" name="makeupStaffType" value="main" checked={makeupStaffType === 'main'}
                    onChange={() => setMakeupStaffType('main')} className="accent-blue-600" />
                  본식
                </label>
                <input type="text" placeholder="담당자명" value={makeupStaffSearch}
                  onChange={(e) => { setMakeupStaffSearch(e.target.value); setPage(1); }}
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[100px]" />
              </div>
            </div>
            {/* 헤어담당 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">헤어담당</label>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-sm">
                  <input type="radio" name="hairStaffType" value="rehearsal" checked={hairStaffType === 'rehearsal'}
                    onChange={() => setHairStaffType('rehearsal')} className="accent-blue-600" />
                  리허설
                </label>
                <label className="flex items-center gap-1 text-sm">
                  <input type="radio" name="hairStaffType" value="main" checked={hairStaffType === 'main'}
                    onChange={() => setHairStaffType('main')} className="accent-blue-600" />
                  본식
                </label>
                <input type="text" placeholder="담당자명" value={hairStaffSearch}
                  onChange={(e) => { setHairStaffSearch(e.target.value); setPage(1); }}
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[100px]" />
              </div>
            </div>
          </div>
          {/* Row 2 */}
          <div className="flex items-end gap-4 flex-wrap mt-3">
            {/* 조회기간 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">조회기간</label>
              <div className="flex items-center gap-2 flex-wrap">
                {DATE_CRITERIA.map(c => (
                  <label key={c.value} className="flex items-center gap-1 text-xs">
                    <input type="radio" name="dateCriteria" value={c.value} checked={dateCriteria === c.value}
                      onChange={() => { setDateCriteria(c.value); setPage(1); }} className="accent-blue-600" />
                    {c.label}
                  </label>
                ))}
                <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <span className="text-gray-400">~</span>
                <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            {/* 이름조회 */}
            <div className="flex-1 min-w-[250px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">이름조회</label>
              <div className="flex items-center gap-2">
                <select value={searchCriteria} onChange={(e) => { setSearchCriteria(e.target.value); setPage(1); }}
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[90px]">
                  {SEARCH_CRITERIA.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input type="text" placeholder="검색어 입력" value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 검색 결과 카운트 */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-600">
            총 <span className="font-semibold text-gray-800">{total}</span>건
          </div>
        </div>

        {/* 고객 목록 테이블 */}
        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : customers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-brush-line text-5xl block mb-3"></i>등록된 고객이 없습니다.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs">
                    <th className="px-3 py-3 text-left font-medium">등록일</th>
                    <th className="px-3 py-3 text-left font-medium">발주일</th>
                    <th className="px-3 py-3 text-left font-medium">고객명</th>
                    <th className="px-3 py-3 text-left font-medium">업체명</th>
                    <th className="px-3 py-3 text-center font-medium">구분</th>
                    <th className="px-3 py-3 text-center font-medium">메이크업(리/본)</th>
                    <th className="px-3 py-3 text-center font-medium">헤어(리/본)</th>
                    <th className="px-3 py-3 text-center font-medium">리허설일</th>
                    <th className="px-3 py-3 text-center font-medium">본식일</th>
                    <th className="px-3 py-3 text-center font-medium">상태</th>
                    <th className="px-3 py-3 text-left font-medium">비고</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map((c) => {
                    const weddingDays = daysUntil(c.weddingDt);
                    return (
                      <tr key={c.makeupCustomerIdx}
                        className="hover:bg-blue-50/30 cursor-pointer transition-colors"
                        onClick={() => navigate(`/makeup-shop/customer-detail?id=${c.makeupCustomerIdx}`)}>
                        <td className="px-3 py-3 text-xs text-gray-500">{formatDate(c.createDt)}</td>
                        <td className="px-3 py-3 text-xs text-gray-500">{c.orderDt ? formatDate(c.orderDt) : <span className="text-gray-300">미정</span>}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-col">
                            {c.groomName && <span className="text-xs"><i className="ri-men-line text-blue-500 mr-1"></i>{c.groomName}</span>}
                            {c.brideName && <span className="text-xs"><i className="ri-women-line text-pink-500 mr-1"></i>{c.brideName}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-600">{c.partnerVendorName || <span className="text-gray-300">워킹고객</span>}</td>
                        <td className="px-3 py-3 text-center">
                          {c.customerType && (
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${CUSTOMER_TYPE_COLORS[c.customerType] || 'bg-gray-100 text-gray-700'}`}>
                              {CUSTOMER_TYPES.find(t => t.value === c.customerType)?.label}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center text-xs">
                          <div className="flex flex-col items-center">
                            <span className="text-gray-600">{c.rehearsalMakeupStaff?.name || <span className="text-gray-300">-</span>}</span>
                            <span className="text-gray-400">{c.mainMakeupStaff?.name || <span className="text-gray-300">-</span>}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center text-xs">
                          <div className="flex flex-col items-center">
                            <span className="text-gray-600">{c.rehearsalHairStaff?.name || <span className="text-gray-300">-</span>}</span>
                            <span className="text-gray-400">{c.mainHairStaff?.name || <span className="text-gray-300">-</span>}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center text-xs">{formatDate(c.rehearsalDt)}</td>
                        <td className="px-3 py-3 text-center">
                          <div className="text-xs font-medium">{formatDate(c.weddingDt)}</div>
                          {weddingDays !== null && (
                            <div className={`text-[10px] font-bold ${weddingDays <= 14 ? 'text-red-500' : weddingDays <= 30 ? 'text-orange-500' : 'text-gray-400'}`}>
                              D-{weddingDays}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${CUSTOMER_STATUS_COLORS[c.customerStatus] || 'bg-gray-100 text-gray-700'}`}>
                            {c.customerStatus}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-400 max-w-[120px] truncate">{c.memo || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm cursor-pointer ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        {/* 고객 등록 모달 */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">신규 고객 등록</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-6">
                {/* 신랑 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-men-line text-blue-500"></i> 신랑 정보
                  </h3>
                  <p className="text-xs text-gray-400 mb-2 -mt-2">신랑 또는 신부 중 한 명의 이름과 연락처는 필수입니다</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신랑 이름 <span className="text-red-500">*</span></label>
                      <input type="text" value={form.groomName} onChange={(e) => setForm({ ...form, groomName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신랑 연락처 <span className="text-red-500">*</span></label>
                      <input type="tel" value={form.groomPhone} onChange={(e) => setForm({ ...form, groomPhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                    </div>
                  </div>
                </div>
                {/* 신부 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-women-line text-pink-500"></i> 신부 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신부 이름 <span className="text-red-500">*</span></label>
                      <input type="text" value={form.brideName} onChange={(e) => setForm({ ...form, brideName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신부 연락처 <span className="text-red-500">*</span></label>
                      <input type="tel" value={form.bridePhone} onChange={(e) => setForm({ ...form, bridePhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                    </div>
                  </div>
                </div>
                {/* 메이크업 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-brush-line text-gray-400"></i> 메이크업 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객 구분</label>
                      <select value={form.customerType} onChange={(e) => setForm({ ...form, customerType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {CUSTOMER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">샵/출장 구분</label>
                      <select value={form.scheduleType} onChange={(e) => setForm({ ...form, scheduleType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {SCHEDULE_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식장소</label>
                      <input type="text" value={form.weddingVenue} onChange={(e) => setForm({ ...form, weddingVenue: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">드레스샵명</label>
                      <input type="text" value={form.dressShopName} onChange={(e) => setForm({ ...form, dressShopName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
                {/* 일정 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-calendar-heart-line text-gray-400"></i> 일정 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">발주일</label>
                      <input type="datetime-local" value={form.orderDt} onChange={(e) => setForm({ ...form, orderDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">리허설일</label>
                      <input type="datetime-local" value={form.rehearsalDt} onChange={(e) => setForm({ ...form, rehearsalDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식일</label>
                      <input type="datetime-local" value={form.weddingDt} onChange={(e) => setForm({ ...form, weddingDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
                {/* 혼주 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-group-line text-gray-400"></i> 혼주 정보
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">혼주정보 (JSON)</label>
                    <textarea value={form.honjuInfo} onChange={(e) => setForm({ ...form, honjuInfo: e.target.value })} rows={3}
                      placeholder='예: {"신랑측": {"어머니": true, "아버지": false}, "신부측": {"어머니": true}}'
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-mono text-xs" />
                  </div>
                </div>
                {/* 유입 경로 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-route-line text-gray-400"></i> 유입 경로
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입경로</label>
                      <select value={form.referralSource}
                        onChange={(e) => setForm({ ...form, referralSource: e.target.value, referralSourceDetail: '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {REFERRAL_SOURCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입경로 상세</label>
                      {referralDetailOptions.length > 0 ? (
                        <select value={form.referralSourceDetail}
                          onChange={(e) => setForm({ ...form, referralSourceDetail: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          {referralDetailOptions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                      ) : (
                        <input type="text" value={form.referralSourceDetail}
                          onChange={(e) => setForm({ ...form, referralSourceDetail: e.target.value })}
                          placeholder={form.referralSource ? '직접 입력' : '유입경로를 먼저 선택하세요'}
                          disabled={!form.referralSource}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer">취소</button>
                  <button type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
